import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { getQuotesSummary } from '../../../shared/api/quotesSelection.api'
import type { RequestItem } from '../../../shared/api/types'
import { resolveKpSupplier } from '../utils/computeBestPrice'
import type { MatrixRates } from '../utils/convertQuoteToMatrixField'
import { withQuotePrices } from './useKpBestPrices'

type Params = {
  requestId: string | undefined
  /** Все позиции заявки из карточки (квоты в них — без цен). */
  items: RequestItem[]
  kpMode: boolean
  /** Курсы USD/EUR/CNY — для сверки цены квоты с ценой в ячейке. */
  quoteRates: MatrixRates | null
  onSelect: (
    itemId: string,
    col: string,
    matrix: string,
    cost: number,
    supplierName: string | null,
  ) => void
}

/**
 * Отпечаток того, от чего зависит сверка «квота ↔ ячейка»: цены в ячейках
 * матриц и состав квот позиций. Поменялся — сводка с ценами квот устарела.
 */
export function quotesFingerprint(items: RequestItem[]): string {
  return items
    .map((i) =>
      [
        i.id,
        i.priceStock,
        i.priceManufacturer,
        i.priceOther,
        i.priceRF,
        i.targetPrice,
        (i.results ?? []).map((r) => `${r.sourceType}/${r.supplierName}`).join(','),
      ].join(':'),
    )
    .join('|')
}

/**
 * Клик по ячейке матрицы в режиме КП (№73): строка КП подписывается
 * поставщиком той квоты, чья цена лежит в ячейке, — то же правило, что у
 * кнопок «По лучшим ценам» и «Выбрать все позиции» (resolveKpSupplier).
 *
 * В карточке заявки квоты приходят без цен, поэтому цены берутся из сводки
 * «Выбор квот» — её подгружает уже вход в режим КП (useEnterKpMode). Клик
 * берёт сводку из кэша без запроса; сводку заново запрашивает, только если её
 * ещё нет или с загрузки поменялись цены в ячейках либо квоты позиций (отпечаток
 * ниже). Сводка не загрузилась — прежнее правило, без ошибки.
 *
 * Колонки таблицы мемоизированы без колбэков в зависимостях (useItemColDefs),
 * поэтому обработчик стабилен, а свежие позиции и курсы читает из ref.
 */
export function useKpCellSelect({ requestId, items, kpMode, quoteRates, onSelect }: Params) {
  const queryClient = useQueryClient()

  const latest = useRef({ items, kpMode, quoteRates })
  useEffect(() => {
    latest.current = { items, kpMode, quoteRates }
  }, [items, kpMode, quoteRates])

  // Цены или квоты позиций поменялись (правка, новая квота по WS) — сводка в
  // кэше устарела. Без перезапроса: следующий клик сам её перечитает.
  const fingerprint = useMemo(() => quotesFingerprint(items), [items])
  const seenFingerprint = useRef(fingerprint)
  useEffect(() => {
    if (!requestId || seenFingerprint.current === fingerprint) return
    seenFingerprint.current = fingerprint
    void queryClient.invalidateQueries({
      queryKey: ['quotes-summary', requestId],
      refetchType: 'none',
    })
  }, [fingerprint, requestId, queryClient])

  return useCallback(
    async (itemId: string, col: string, matrix: string, cost: number, cellValue: number) => {
      let item = latest.current.items.find((i) => i.id === itemId)
      if (requestId && item) {
        try {
          // staleTime: Infinity — свежесть сводки держит отпечаток выше: без
          // изменений клик не ходит на сервер, после изменений — перечитывает.
          const summary = await queryClient.fetchQuery({
            queryKey: ['quotes-summary', requestId],
            queryFn: () => getQuotesSummary(requestId),
            staleTime: Infinity,
            retry: false,
          })
          item = withQuotePrices([item], summary.positions)[0]
        } catch {
          // Сводка не загрузилась — поставщик по прежнему правилу.
        }
      }
      // Режим КП закрыли, пока грузилась сводка, — выбор не трогаем.
      if (!latest.current.kpMode) return
      const supplierName = resolveKpSupplier(
        item?.results,
        matrix,
        cellValue,
        latest.current.quoteRates,
      )
      onSelect(itemId, col, matrix, cost, supplierName)
    },
    [requestId, queryClient, onSelect],
  )
}
